import React, { useState, useEffect } from 'react';
import { Container, Paper, Box, Typography, TextField, Button, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import * as endPoints from '../assets/js/endpoints';
import { useNotification } from '../assets/contexts/notificationContext';
import PageTitle from '../components/PageTitle';
import { Helmet } from 'react-helmet';

const dayLabels = {
  monday: 'Lunes',
  tuesday: 'Martes',
  wednesday: 'Miércoles',
  thursday: 'Jueves',
  friday: 'Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo',
};

const UserAvailability = () => {
  const [availability, setAvailability] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  });
  const [duration, setDuration] = useState('');
  const [courtesy, setCourtesy] = useState('');
  const [exceptions, setExceptions] = useState([]);
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const weekdayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const weekendKeys = ['saturday', 'sunday'];

  useEffect(() => {
    const load = async () => {
      try {
        const data = await endPoints.getAvailability();
        if (data) {
          setAvailability(prev => ({ ...prev, ...(data.days || {}) }));
          if (data.duration) setDuration(data.duration);
          if (data.courtesy) setCourtesy(data.courtesy);
          if (Array.isArray(data.exceptions)) {
            setExceptions(data.exceptions.map(item => ({
              id: item.id,
              date: item.date || '',
              start: item.start || '',
              end: item.end || '',
            })));
          }
        }
      } catch (_e) {
      }
    };
    load();
  }, []);

  const handleChange = (day, index, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: prev[day].map((slot, i) => i === index ? { ...slot, [field]: value } : slot),
    }));
  };

  const addSlot = day => {
    setAvailability(prev => ({
      ...prev,
      [day]: [...prev[day], { start: '', end: '' }],
    }));
  };

  const removeSlot = (day, index) => {
    setAvailability(prev => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      days: availability,
      duration,
      courtesy,
      exceptions,
    };
    try {
      await endPoints.updateAvailability(payload);
      showNotification('Disponibilidad actualizada', 'success');
    } catch (_err) {
      showNotification('Error guardando disponibilidad', 'error');
    } finally {
      setLoading(false);
    }
  };

  const addException = () => {
    setExceptions(prev => [...prev, { date: '', start: '', end: '' }]);
  };

  const updateException = (index, field, value) => {
    setExceptions(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const removeException = index => {
    setExceptions(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <Helmet>
        <title>Mi Disponibilidad | CES</title>
      </Helmet>
      <Container sx={{ mt: 3, display: 'flex', justifyContent: 'center', flexDirection: 'column', width: '100%' }}>
       <Box sx={{ mb: 3 }}>
            <PageTitle>Mi Disponibilidad</PageTitle>
          </Box> 
        <Paper
          sx={{
            p: 2,
            width: '100%',
          }}
        >
          
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(1, 4fr)' }, alignItems: 'stretch' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                {weekdayKeys.map(day => (
                  <Box key={day} sx={{ p: 2, borderRadius: 1, border: theme => `1px solid ${theme.palette.divider}`,  }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>{dayLabels[day]}</Typography>
                    {availability[day].map((slot, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 1,  }}>
                        <TextField
                          type="time"
                          value={slot.start}
                          onChange={e => handleChange(day, idx, 'start', e.target.value)}
                          label="Inicio"
                          InputLabelProps={{ shrink: true }}
                          required
                          sx={{ flex: '1 1 120px' }}
                        />
                        <TextField
                          type="time"
                          value={slot.end}
                          onChange={e => handleChange(day, idx, 'end', e.target.value)}
                          label="Fin"
                          InputLabelProps={{ shrink: true }}
                          required
                          sx={{ flex: '1 1 120px' }}
                        />
                        <IconButton onClick={() => removeSlot(day, idx)} color="error" sx={{ flexShrink: 0 }}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    {availability[day].length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No disponible
                      </Typography>
                    )}
                    <Button
                      type="button"
                      startIcon={<AddIcon />}
                      onClick={() => addSlot(day)}
                      size="small"
                      sx={{ p: 0, mt: 1, alignSelf: 'flex-start' }}
                    >
                      Añadir franja
                    </Button>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'center' }}>
                {weekendKeys.map(day => (
                  <Box key={day} sx={{ p: 2, borderRadius: 1, border: theme => `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>{dayLabels[day]}</Typography>
                    {availability[day].map((slot, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                        <TextField
                          type="time"
                          value={slot.start}
                          onChange={e => handleChange(day, idx, 'start', e.target.value)}
                          label="Inicio"
                          InputLabelProps={{ shrink: true }}
                          required
                          sx={{ flex: '1 1 120px' }}
                        />
                        <TextField
                          type="time"
                          value={slot.end}
                          onChange={e => handleChange(day, idx, 'end', e.target.value)}
                          label="Fin"
                          InputLabelProps={{ shrink: true }}
                          required
                          sx={{ flex: '1 1 120px' }}
                        />
                        <IconButton onClick={() => removeSlot(day, idx)} color="error" sx={{ flexShrink: 0 }}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    {availability[day].length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No disponible
                      </Typography>
                    )}
                    <Button
                      type="button"
                      startIcon={<AddIcon />}
                      onClick={() => addSlot(day)}
                      size="small"
                      sx={{ p: 0, mt: 1, alignSelf: 'flex-start' }}
                    >
                      Añadir franja
                    </Button>
                  </Box>
                ))}
              </Box>
              <Box sx={{ p: 2, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 2,  }}>
                <Typography variant="h6" sx={{alignSelf:'center'}}>Configuración</Typography>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', gap: 3, justifyContent: 'center' }}>
                <TextField
                  label="Duración por cita (min)"
                  type="number"
                  value={duration}
                  onChange={e => setDuration(e.target.value)}
                  required
                />
                <TextField
                  label="Minutos de cortesía"
                  type="number"
                  value={courtesy}
                  onChange={e => setCourtesy(e.target.value)}
                  required                  
                />
                 </Box>
              </Box>
              <Box sx={{ p: 2, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ alignSelf: 'center' }}>Excepciones puntuales</Typography>
                {exceptions.map((exception, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
                    <TextField
                      type="date"
                      value={exception.date}
                      onChange={e => updateException(idx, 'date', e.target.value)}
                      label="Fecha"
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                    <TextField
                      type="time"
                      value={exception.start}
                      onChange={e => updateException(idx, 'start', e.target.value)}
                      label="Desde (opcional)"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      type="time"
                      value={exception.end}
                      onChange={e => updateException(idx, 'end', e.target.value)}
                      label="Hasta (opcional)"
                      InputLabelProps={{ shrink: true }}
                    />
                    <IconButton onClick={() => removeException(idx)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Si dejas "Desde" y "Hasta" vacíos, ese día quedará bloqueado completo.
                </Typography>
                <Button type="button" startIcon={<AddIcon />} onClick={addException} size="small" sx={{ alignSelf: 'center' }}>
                  Añadir excepción
                </Button>
              </Box>
              <Box sx={{ p: 2, borderRadius: 1,  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 'fit-content', justifySelf: 'center' }}>
                <Button type="submit" variant="contained" fullWidth disabled={loading}>
                  Guardar
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default UserAvailability;
